import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { SelectionModel } from "@angular/cdk/collections";
import { ToastrService } from "ngx-toastr";
import { TranslocoService } from "@ngneat/transloco";
import { take } from "rxjs/operators";
import * as _ from "lodash";

import { Compbaser } from "../compbaser";
import {
    AccessService,
    AppService,
    LocalStoreService,
    SceneService,
    StoreSelectorsService,
    UserService,
} from "../../services";
import {
    IAppSettingsWrap,
    IPlayerProps,
    IResource,
    ISocialToken,
    IUser,
    EnumConnectionType,
    IStationData, ExperienceLevelType,
} from "../../interfaces";
import {
    copyTextClipboard,
    createScenePreviewUrl,
    popupWindow,
    TOAST_FASTER,
    convertBooleanToNumbers,
    isNullOrUndefOrEmpty,
} from "../../common";
import { actionSocialPost } from "../../store/actions/user.actions";
import { DialogQrGenerateComponent } from "../dialog-qr-generate/dialog-qr-generate.component";
import { DialogCodeComponent } from "../dialog-code/dialog-code.component";
import { DialogPlayerDevicesComponent } from "../dialog-player-devices/dialog-player-devices.component";
import { StationAddPlayerComponent } from "../../containers/stations/station-add-player/station-add-player.component";
import { Lib } from "../../../libs/Lib";

export interface DialogPreviewData {
    selectedSceneId: string;
    previewSceneId?: string;
    sceneData: IPlayerProps;
    editorWidth: number;
    editorHeight: number;
    isMainScene: boolean;
    sceneMimeShort: string;
    coreAppData: IAppSettingsWrap;
    isFullFeatures: boolean;
    isSlider?: boolean;
    sliderName?: string;
    businessId?: any;
}

export interface DialogPreviewResult {
    action?: "register" | "embedded" | "copyLink" | "qrCode" | "socialPost";
    data?: any;
}

export interface DialogPreviewSettings {
    previewMode: "size" | "percent";
    previewSizeX: number;
    previewSizeY: number;
    previewPercent: number;
    previewSceneId?: string;
}

@Component({
    selector: "app-dialog-preview",
    templateUrl: "./dialog-preview.component.html",
    styleUrls: ["./dialog-preview.component.scss"],
})
export class DialogPreviewComponent extends Compbaser implements OnInit {

    // Preview properties
    previewSizeX: number;
    previewSizeY: number;
    previewPercent = 100;
    previewMode = "size";
    previewSceneId: string | null = null;
    readonly percentages = [25, 50, 75, 100, 150, 200];
    rememberSelection = true;

    // Social media properties
    socialMediaSelection: SelectionModel<string>;
    socialMediaResource: IResource;
    socialMediaResourceName = "";
    socialMediaTitle = "";
    socialPostDisabled = true;
    socialPosting = false;
    canShowDownload = true;
    user: IUser;

    @ViewChild("appSceneSocialTokenApp", { static: false }) appSceneSocialTokenApp2;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: DialogPreviewData,
        private dialogRef: MatDialogRef<DialogPreviewComponent>,
        public override dialog: MatDialog,
        private toastr: ToastrService,
        private translocoService: TranslocoService,
        private localStoreService: LocalStoreService,
        private storeSelector: StoreSelectorsService,
        private accessService: AccessService,
        private appService: AppService,
        private sceneService: SceneService,
        private userService: UserService,
    ) {
        super(dialog);
        this.socialMediaSelection = new SelectionModel<string>(true, []);
        this.previewSceneId = data.previewSceneId || data.selectedSceneId;
    }

    ngOnInit(): void {
        // Initialize preview settings from local storage
        const storedRememberSetting = this.localStoreService.getStorageValConfig("rememberPreviewSettings");
        console.log("Debug - storedRememberSetting raw:", storedRememberSetting);

        if (storedRememberSetting === null || storedRememberSetting === undefined || storedRememberSetting === "") {
            // Default to true for first-time users
            console.log("Debug - No stored setting, defaulting to true");
            this.rememberSelection = true;
            const v = convertBooleanToNumbers(this.rememberSelection, true);
            this.localStoreService.setStorageValConfig("rememberPreviewSettings", v);
        } else {
            this.rememberSelection = storedRememberSetting === "1";
            console.log("Debug - Found stored setting:", storedRememberSetting, "-> rememberSelection:", this.rememberSelection);
        }

        // console.log('Debug - final rememberSelection:', this.rememberSelection);
        // console.log('Debug - stored previewMode:', this.localStoreService.getStorageValConfig('previewMode'));
        // console.log('Debug - stored previewPercent:', this.localStoreService.getStorageValConfig('previewPercent'));

        this.cod(
            this.storeSelector.getUser().subscribe((i_user: IUser) => {
                const accountVersion = Lib.GetAccountVersion(i_user);
                this.canShowDownload = (accountVersion == "FREE" || accountVersion == "PLUS" || accountVersion == "DASH");
            })
        );

        this.loadSettings();

        console.log("Debug - after load - previewMode:", this.previewMode);
        console.log("Debug - after load - previewPercent:", this.previewPercent);

        // Get user data
        this.cod(
            this.storeSelector.getUser().subscribe((user: IUser) => {
                this.user = user;
            }),
        );

        this.socialPostDisabled = true;
    }

    private loadSettings(): void {
        // Always load all UI state regardless of remember checkbox
        const storedMode = this.localStoreService.getStorageValConfig("previewMode");
        const storedSizeX = this.localStoreService.getStorageValConfig("previewSizeX");
        const storedSizeY = this.localStoreService.getStorageValConfig("previewSizeY");
        const storedPercent = this.localStoreService.getStorageValConfig("previewPercent");
        const storedPreviewSceneId = this.localStoreService.getStorageValConfig("previewSceneId");

        console.log("Debug - loadSettings - all stored values:", {
            storedMode, storedSizeX, storedSizeY, storedPercent, storedPreviewSceneId,
        });

        // Load radio button state
        if (storedMode === "size" || storedMode === "percent") {
            this.previewMode = storedMode;
            console.log("Debug - loadSettings - set previewMode to:", this.previewMode);
        }

        // Load all values
        if (storedSizeX) this.previewSizeX = parseInt(storedSizeX);
        if (storedSizeY) this.previewSizeY = parseInt(storedSizeY);
        if (storedPercent) this.previewPercent = parseInt(storedPercent);

        if (storedPreviewSceneId && !this.data.isSlider) {
            this.previewSceneId = storedPreviewSceneId;
        }

        // Set default preview size if not set
        if (!this.previewSizeX) {
            this.previewSizeX = this.data.editorWidth;
        }
        if (!this.previewSizeY) {
            this.previewSizeY = this.data.editorHeight;
        }

        // Ensure percentage is valid
        if (!this.previewPercent || !this.percentages.includes(this.previewPercent)) {
            this.previewPercent = 100;
        }

        console.log("Debug - loadSettings - final values:", {
            previewMode: this.previewMode,
            previewPercent: this.previewPercent,
            previewSizeX: this.previewSizeX,
            previewSizeY: this.previewSizeY,
        });
    }

    createSharedUrl(): string {
        const sceneId = this.previewSceneId ?? this.data.selectedSceneId;
        const businessId = this.data.businessId ?? null;
        return createScenePreviewUrl(this.data.coreAppData, businessId, sceneId, this.data.isFullFeatures);
    }

    changePreviewScene(sceneId: string): void {
        this.previewSceneId = sceneId;
    }

    onClickPlayPreview(): void {
        let w = 0, h = 0;
        if (this.previewMode === "size") {
            w = parseInt(this.previewSizeX.toString());
            h = parseInt(this.previewSizeY.toString());
        } else {
            w = Math.round(this.data.editorWidth * this.previewPercent / 100);
            h = Math.round(this.data.editorHeight * this.previewPercent / 100);
        }
        if (!w || !h) {
            return;
        }
        popupWindow({ url: this.createSharedUrl(), title: "_blank", w, h }, "yes", "yes");
        this.closeDialog();
    }

    onRegisterPlayer(): void {
        if (!this.data.isMainScene) return;

        this.dialogRef.close({ action: "register", data: { sceneId: this.data.selectedSceneId } });
    }

    onWebEmbedded(): void {
        if (!this.user?.enterprise) {
            // Show upgrade dialog
            this.toastr.warning("This feature requires enterprise subscription");
            return;
        }
        this.dialog.open(DialogCodeComponent, {
            width: "500px",
            height: "800px",
            data: { sceneId: this.previewSceneId || this.data.selectedSceneId, codeTemplateId: "2" },
        });
    }

    onCopyLink(): void {
        copyTextClipboard(this.createSharedUrl());
        this.toastr.info(
            this.translocoService.translate("toast.copiedToClipboard", {}, this.translocoService.getActiveLang()),
            "",
            TOAST_FASTER,
        );
    }

    onQrCode(): void {
        this.dialog.open(DialogQrGenerateComponent, {
            height: "400px",
            width: "400px",
            data: { url: this.createSharedUrl() },
        });
    }

    onGetPlayer(): void {
        this.dialog.open(DialogPlayerDevicesComponent, {
            width: "750px",
            maxHeight: "90vh",
            data: {},
        });
    }

    changeRemeberSelection(): void {
        const v = convertBooleanToNumbers(this.rememberSelection, true);
        this.localStoreService.setStorageValConfig("rememberPreviewSettings", v);
        if (this.rememberSelection) {
            this.saveSettings();
        }
    }

    onPreviewModeChange(): void {
        console.log("Debug - onPreviewModeChange called, previewMode:", this.previewMode);
        // Always save the mode selection (radio button state)
        this.saveSettings();
    }

    onPreviewPercentChange(): void {
        console.log("Debug - onPreviewPercentChange called, previewPercent:", this.previewPercent);
        // Always save when percentage changes
        this.saveSettings();
    }

    onPreviewSizeChange(): void {
        console.log("Debug - onPreviewSizeChange called, size:", this.previewSizeX, "x", this.previewSizeY);
        // Always save when size changes
        this.saveSettings();
    }

    private saveSettings(): void {
        console.log("Debug - saveSettings called, rememberSelection:", this.rememberSelection);

        // Always save UI state (radio buttons, dropdowns, inputs)
        let sceneId = "";
        if (this.previewSceneId != this.data.selectedSceneId && !this.data.isSlider) {
            sceneId = this.previewSceneId;
        }

        this.localStoreService.setStorageValConfigGroup([
            { key: "previewMode", val: this.previewMode },
            { key: "previewSizeX", val: `${this.previewSizeX}` },
            { key: "previewSizeY", val: `${this.previewSizeY}` },
            { key: "previewPercent", val: `${this.previewPercent}` },
            { key: "previewSceneId", val: sceneId },
        ]);

        console.log("Debug - saved all UI state:", {
            previewMode: this.previewMode,
            previewPercent: this.previewPercent,
            previewSizeX: this.previewSizeX,
            previewSizeY: this.previewSizeY,
        });
    }

    // Social Media Methods
    selectSocialMedia(checked: boolean, value: string): void {
        if (checked) {
            this.socialMediaSelection.select(value);
        } else {
            this.socialMediaSelection.deselect(value);
        }
        this.isSocialPostReady();
    }

    selectResource(): void {
        this.sceneService.openAssetDialog("RESOURCE:ALL", false, (v) => {
            if (v) {
                this.socialMediaResource = v.sourceData;
                this.socialMediaResourceName = `${this.socialMediaResource.resource_name}.${this.socialMediaResource.resource_type}`;
                this.isSocialPostReady();
            }
        });
    }

    isSocialPostReady(): boolean {
        if (isNullOrUndefOrEmpty(this.socialMediaResource)) {
            this.socialPostDisabled = true;
            return false;
        }
        if (isNullOrUndefOrEmpty(this.socialMediaTitle)) {
            this.socialPostDisabled = true;
            return false;
        }
        if (isNullOrUndefOrEmpty(this.appSceneSocialTokenApp2?.socialKey)) {
            this.socialPostDisabled = true;
            return false;
        }
        if (this.socialMediaSelection.selected.length === 0) {
            this.socialPostDisabled = true;
            return false;
        }

        this.socialPostDisabled = false;
        return true;
    }

    onSocialKeyChanged(e: any): void {
        this.isSocialPostReady();
    }

    postToSocial(): void {
        if (Lib.IsAccessBlocked(this.user)) {
            this.toastr.error(
                this.translocoService.translate("account.screenDisruption", {}, this.translocoService.getActiveLang()),
                "[ Contact live chat ]",
            );
            return;
        }

        if (!this.isSocialPostReady()) {
            return;
        }

        this.socialPosting = true;
        this.cod(
            this.storeSelector.getGlobalSettings("ayreshare_token")
                .pipe(take(1))
                .subscribe((globalSettings) => {
                    const a: ISocialToken | any = globalSettings[0]?.param_value;
                    let socialKey: ISocialToken;

                    try {
                        socialKey = JSON.parse(a);
                    } catch (e) {
                        socialKey = this.appSceneSocialTokenApp2.socialKey;
                    }

                    if (!socialKey) {
                        this.toastr.error("Social media token not found");
                        this.socialPosting = false;
                        return;
                    }

                    this.storeSelector.dispatch(actionSocialPost({
                        socialKey,
                        platforms: this.socialMediaSelection.selected,
                        text: this.socialMediaTitle,
                        resourceUrl: this.socialMediaResource.resource_name,
                        previewUrl: this.createSharedUrl(),
                    }));
                }),
        );
    }

    private closeDialog(): void {
        this.saveSettings();
        this.dialogRef.close();
    }
}
